import React from "react";
import ReactDOM from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import {
  BrowserCacheLocation,
  PublicClientApplication,
} from "@azure/msal-browser";
// w3.css is now imported INSIDE index.css with `layer(components)` (Tailwind 4
// puts utilities in a native @layer; an unlayered w3.css would override every
// Tailwind utility — e.g. its `button { width: 100% }` broke the header nav).
import "./index.css";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import { AppContextProvider } from "./context/app.context";
// ModelRwContextProvider removed - Realm EOL Jan 13, 2026
// File kept for reference: ./context/modelRW.context.js
import { ModelCxContextProvider } from "./context/modelCx.context";
import { ScheduleContextProvider } from "./context/schedule.context";
// Convex experiment (#191) — dormant unless REACT_APP_BACKEND=convex.
import ConvexClientProvider from "./convex/ConvexClientProvider";
import ConvexBridge from "./context/ConvexBridge";
import ConvexLiveBadge from "./context/ConvexLiveBadge";
import { FavInboxProvider } from "./context/favInbox.context";
import { CONVEX_CONFIGURED } from "./utility/backendConfig";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// ReactQueryDevtools moved to App.js - conditional on user's devTools setting
import { APP_VERSION } from "./utility/keys";
import { isMsalAuthResponseWindow } from "./utility/isMsalAuthResponseWindow";

// Log app version immediately on load
console.log(
  "%c🚀 Scheduler App Version: " + APP_VERSION,
  "color: #00FF00; font-weight: bold; font-size: 14px; background-color: #1a1a1a; padding: 4px 8px; border-radius: 4px;"
);

// MSAL configuration - Microsoft Entra (Azure AD) Authentication
// App Registration: "Scheduler" in Azure Portal
// clientId: Application (client) ID from Azure app registration
// authority: Directory (tenant) ID - Keller Interiors tenant
//
// IMPORTANT - Client Secret "Scheduler_JWT":
// - This React SPA uses PublicClientApplication (OAuth implicit/PKCE flow)
// - SPAs DO NOT use client secrets in frontend code (security - would be exposed in browser)
// - The "Scheduler_JWT" client secret is stored ELSEWHERE (AWS Lambda, backend API, or Azure Key Vault)
// - Client secret expires periodically (e.g., every 24 months) and must be renewed in Azure Portal
// - When expired, backend token validation fails (users can't authenticate)
// - To renew: Azure Portal > App Registrations > Scheduler > Certificates & secrets > New client secret
// - Update the secret in the backend service (Lambda environment variables, Key Vault, etc.) - NOT here
//
// Azure Portal: https://portal.azure.com > Microsoft Entra ID > App Registrations > Scheduler
const configuration = {
  auth: {
    clientId: "4ae8c1ac-41df-46bb-a881-011a10d1b09b",
    authority:
      "https://login.microsoftonline.com/3d199282-cde8-4234-bab6-ef614f1313af",
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
};

// - - - - - - - - - -
// #508 — MSAL v5 popup / silent-iframe hand-off.
//
// v5 no longer lets the main window read a popup's URL. When Microsoft sends a
// popup (or the hidden silent-renewal iframe) back to our redirect URI — this
// same app — that page must hand the result to the main window itself via
// `broadcastResponseToMainFrame()`. Booting the whole Scheduler in there instead
// (what v4 tolerated) leaves the main window on "Sign In with Microsoft"
// forever: measured on UAT 2026-09-28.
//
// So: if THIS window is that popup/iframe, run only the bridge and never boot
// the app. The check is a pure, tested helper (isMsalAuthResponseWindow).
// - - - - - - - - - -
if (isMsalAuthResponseWindow(window)) {
  import("@azure/msal-browser/redirect-bridge")
    .then(({ broadcastResponseToMainFrame }) => broadcastResponseToMainFrame())
    .catch((error) => {
      console.error("index.js MSAL redirect bridge failed:", error);
    });
} else {
  bootApp();
}

function bootApp() {
  const msalInstance = new PublicClientApplication(configuration);

  msalInstance.initialize().then(() => {
    console.log("index.js MSAL initialize");
    // Default to using the first account if no account is active on page load

    if (
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      // Account selection logic is app dependent. Adjust as needed for different use cases.
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }

    // Create a QueryClient instance
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 5 * 1000, // 5 seconds: data is fresh for 5 seconds
          cacheTime: 30 * 1000, // 30 seconds: data stays in cache for 30 seconds after becoming stale
          retry: 2, // Retry failed queries up to 2 times
          refetchOnWindowFocus: false, // Disabled: queries controlled by shouldFetch flags
          refetchOnReconnect: false, // Disabled: queries controlled by shouldFetch flags
          refetchOnMount: false, // Disabled: queries controlled by shouldFetch flags
          refetchInterval: false, // Disabled: queries run once when triggered, no polling
          onError: (error) => {
            console.error("Query error:", error); // Global error handling for queries
          },
        },
        mutations: {
          onError: (error) => {
            console.error("Mutation error:", error); // Global error handling for mutations
          },
        },
      },
    });

    const root = ReactDOM.createRoot(document.getElementById("root"));
    root.render(
      // <React.StrictMode>       NOTE: "React intentionally remounts your components in development"
      <BrowserRouter>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <ConvexClientProvider>
              <AppContextProvider>
                <ModelCxContextProvider>
                  <ScheduleContextProvider>
                    {CONVEX_CONFIGURED && <ConvexBridge />}
                    {CONVEX_CONFIGURED && <ConvexLiveBadge />}
                    <FavInboxProvider>
                      <App />
                    </FavInboxProvider>
                  </ScheduleContextProvider>
                </ModelCxContextProvider>
              </AppContextProvider>
            </ConvexClientProvider>
          </QueryClientProvider>
        </MsalProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  });
}
